/**
 * ▶ Preview in the scene editor (spec 009). While the store previews
 * (`isPlaying`), the canonical player covers the canvas area and plays the
 * in-memory scene from the store's `previewStartMs` at the editor's
 * orientation; the editing affordances underneath are out of reach. The
 * timeline playhead follows the runtime's clock. A timeline jump
 * (`jumpPreview`) re-plays from the new time (`play(ms)`, so every audio cue
 * restarts in step), or while dragging shows that frame paused. At the
 * choice reveal the frame holds with the panel clickable (`holdAtChoices`);
 * clicking an option shows "Chose: …" and ends the preview. Without choices
 * the last frame holds until Stop.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSceneStore } from '../../stores/sceneStore';
import { useMediaSheetStore } from '../../stores/mediaSheetStore';
import type { ChoiceOption } from '../../../../Shared/src/@types';
import { PlayerHost, type PlayerHostHandle } from './PlayerHost';
import styles from './ScenePreview.module.scss';

export const ScenePreview: React.FC = () => {
  const isPlaying = useSceneStore((s) => s.isPlaying);
  const scene = useSceneStore((s) => s.scene);
  const keyframes = useSceneStore((s) => s.keyframes);
  const orientation = useSceneStore((s) => s.previewOrientation);
  const setPlayhead = useSceneStore((s) => s.setPlayhead);
  const holdAtChoices = useSceneStore((s) => s.holdAtChoices);
  const stop = useSceneStore((s) => s.stop);
  const startMs = useSceneStore((s) => s.previewStartMs);
  const jump = useSceneStore((s) => s.previewJump);
  const hostRef = useRef<PlayerHostHandle>(null);

  // A jump requested while this preview runs (not one left from an earlier preview)
  const seenJump = useRef<number | null>(null);
  useEffect(() => {
    if (!isPlaying) {
      seenJump.current = null;
      return;
    }
    const seq = jump?.seq ?? 0;
    if (seenJump.current === null) {
      seenJump.current = seq;
      return;
    }
    if (seq === seenJump.current || !jump) return;
    seenJump.current = seq;
    const host = hostRef.current;
    if (!host) return;
    setToast(null);
    if (jump.hold) {
      host.pause();
      host.seek(jump.ms);
    } else {
      host.play(jump.ms);
    }
  }, [isPlaying, jump]);

  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);
  useEffect(() => {
    if (isPlaying) setToast(null);
  }, [isPlaying]);

  // The scene as it is when Preview starts (editing is off while it plays):
  // the store's one playable-scene selector (spec 017)
  // The spritesheet cuts loaded so far (spec 033): a late one re-plays the scene with it.
  const sheets = useMediaSheetStore((s) => s.sheets);
  const input = useMemo(
    () => (scene ? useSceneStore.getState().getPlayableScene() : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scene, keyframes, sheets],
  );

  const handleChoice = useCallback((choice: ChoiceOption) => {
    const choices = useSceneStore.getState().scene?.choices ?? [];
    const label = choice.text.trim() || `Option ${choices.findIndex((c) => c.id === choice.id) + 1}`;
    stop();
    setToast(`Chose: ${label}`);
  }, [stop]);

  return (
    <>
      {isPlaying && input && (
        <div className={styles.overlay} data-testid="scene-preview">
          <PlayerHost
            ref={hostRef}
            scene={input}
            orientation={orientation}
            playing
            startMs={startMs}
            onPlayhead={setPlayhead}
            onReveal={holdAtChoices}
            onChoice={handleChoice}
          />
        </div>
      )}
      {toast && (
        <div className={styles.toast} role="status" data-testid="preview-toast">
          {toast}
        </div>
      )}
    </>
  );
};

export default ScenePreview;
